<template>
  <div>
    <div class="row">
      <div class="container">
        <h1>{{ $t('footer.statistics') }}</h1>
        <p class="updated-time d-none">{{ $t('views.statistics.updated') }} 30-06-2021 06:50</p>
      </div>

      <template v-if="!loadFailed">
      <div class="container-fluid price-graphics" id="background">
        <div class="container">
          <div class="row price justify-content-between">
            <div class="col-lg-4">
              <h3>{{ $t('views.statistics.assetsFunded') }}</h3>
              <div class="box-price">
                {{loansFundedSum | moneyFormatterWithoutCents}}
              </div>
            </div>
            <div class="col-lg-4">
              <h3>{{ $t('views.statistics.loansFulfilled') }}</h3>
              <div class="box-price">
                {{ totalPaidToInvestorsSum | moneyFormatterWithoutCents }}
              </div>
            </div>
            <div class="col-lg-3">
              <h3>{{ $t('views.statistics.averageInterest') }}</h3>
              <div class="box-price">
                {{ investorsAverage }}%
              </div>
            </div>
          </div>

          <div class="graphics">
            <h3>{{ $t('views.statistics.assetsFunded') }}</h3>
            <div>
              <line-chart v-if="loansFundedData!=null && selectedAssets==='outstanding'" :data-to-render="loansFundedData" />
              <bar-chart v-if="outstandingPortfolioData!=null && selectedAssets==='invested'" :data-to-render="outstandingPortfolioData" />
              <div class="text-center w100">
                <b-form-radio-group
                    v-model="selectedAssets"
                    :options="graphSwitcherOptions"
                    class="radio-assets"
                    button-variant="none"
                    name="radio-btn-outline"
                    buttons
                ></b-form-radio-group>
              </div>
            </div>
          </div>
          <div class="graphics">
            <h3>{{ $t('views.statistics.interestPaidToInvestors') }}</h3>
            <div>
              <line-chart v-if="interestPaidToInvestorData!=null" :data-to-render="interestPaidToInvestorData" />
            </div>
          </div>
        </div>
      </div>

      <div class="container">
          <div class="col-12">
            <doughnut-chart-without-filters v-if="loStatsData" :categories="loStatsData" />
            <div class="col-md-6 mb-5">
              <div class="text-center">
                <h5>{{ $t('views.statistics.totalLoansOutstanding') }}: {{loStatsSum | moneyFormatterWithoutCents}}</h5>
              </div>
              </div>
            <div class="col-md-6"></div>
          </div>
      </div>

      <div class="container">
        <div class="col-12">
          <doughnut-chart-with-filters v-if="statusesData" :categories="statusesData" />
        </div>
      </div>
      </template>
      <div v-else class="container">
        <p class="statistics-load-failed text-center">{{ $t('views.statistics.loadFailed') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import {splitOverviewSums} from '@/helpers/statisticsOverview'
import LineChart from '@/components/charts/LineChart'
import BarChart from '@/components/charts/BarChart'
import DoughnutChartWithoutFilters from '@/components/charts/complex/DoughnutChartWithoutFiltersSection'
import DoughnutChartWithFilters from '@/components/charts/complex/DoughnutChartWithFiltersSection'
import moneyMixin from '../mixins/money'
import imageMixin from '@/mixins/image'

export default {
  components: {LineChart, BarChart, DoughnutChartWithoutFilters, DoughnutChartWithFilters},
  mixins: [moneyMixin, imageMixin],
  data()
  {
    return {
      isLoading: true,
      loadFailed: false,
      investorsAverage: 0.00,
      loansFundedSum: 0.00,
      loStatsSum: 0.00,
      statusesSum:
          {
            buyback_initiated: 0,
            current: 0,
            dpd1_30: 0,
            dpd31_60: 0
          },
      totalPaidToInvestorsSum: 0.00,
      loansFundedData: null,
      outstandingPortfolioData: null,
      interestPaidToInvestorData: null,
      loStatsData: null,
      statusesData: null,
      loColors: [
        '#4CAF50', '#BB86FC', '#30A7FE',
        '#eeb624', '#11f39f', '#3CCE3C',
        '#FFAAAB', '#0b18ea', '#6c0267',
        '#e50404', '#750f2d', '#A66E3C',
        '#DDFC74', '#C9847A', '#BAA898',
        '#3D0C11', '#280003', '#BF6900'
      ],
      selectedAssets: 'invested',
      graphSwitcherOptions: [
        {text: this.$t('views.statistics.datasetLabelCumulative'), value: 'invested'},
        {text: this.$t('views.statistics.datasetLabelActive'), value: 'outstanding'}
      ]
    }
  },
  mounted()
  {
    const axios = require('axios').default
    axios.get(process.env.VUE_APP_API_BASE_URL + 'statistics').then(response =>
    {
      this.statusesData = []
      const loansFundedData = []
      const outstandingPortfolioData = []
      const interestPaidToInvestorData = []
      const loStatsData = []
      // eslint-disable-next-line no-unused-vars
      let statusesData = []
      response.data.activity.map(activity => {
        loansFundedData.push([activity.month, parseFloat(activity.invested)])
        outstandingPortfolioData.push({x: activity.month, y: parseFloat(activity.outstanding)})
        interestPaidToInvestorData.push([activity.month, parseFloat(activity.interest_paid)])
        // this.loansFundedSum = parseInt(activity.outstanding) // latest
        return activity.invested
      })
      this.loansFundedData = loansFundedData
      this.outstandingPortfolioData = outstandingPortfolioData
      this.investorsAverage = response.data.average_earning
      const sums = splitOverviewSums(response.data.overview)
      const activeOverviews = sums.activeRows
      this.loansFundedSum = sums.loansFundedSum
      this.totalPaidToInvestorsSum = sums.totalPaidToInvestorsSum
      this.loStatsSum = sums.loStatsSum
      this.statusesSum = sums.statusesSum
      const totalStatusesAmount = this.statusesSum.current + this.statusesSum.dpd1_30 + this.statusesSum.dpd31_60 + this.statusesSum.buyback_initiated
      activeOverviews.map(overview =>
          {
            // interestPaidToInvestorData.push([overview.loan_originator_name, parseFloat(overview.total_paid_to_investors)])
            loStatsData.push({
              name: overview.loan_originator_name,
              amount: overview.outstanding_to_investors,
              percent: this.getPercent(overview.outstanding_to_investors, this.loStatsSum),
              color: this.loColors.shift()
            })

            statusesData = [
              {
                name: this.$t('views.statistics.labelCurrent'),
                amount: parseInt(overview.current),
                totalPercent: this.getPercent(parseInt(overview.current), totalStatusesAmount),
                percent: this.getPercent(parseInt(overview.current), overview.outstanding_to_investors),
                color: '#3CCE3C'
              },
              {
                name: this.$t('views.statistics.labelOneToThirtyDays'),
                amount: parseInt(overview.dpd1_30),
                totalPercent: this.getPercent(parseInt(overview.dpd1_30), totalStatusesAmount),
                percent: this.getPercent(parseInt(overview.dpd1_30), overview.outstanding_to_investors),
                color: '#4CAF50'
              },
              {
                name: this.$t('views.statistics.labelThirtyToSixtyDays'),
                amount: parseInt(overview.dpd31_60),
                totalPercent: this.getPercent(parseInt(overview.dpd31_60), totalStatusesAmount),
                percent: this.getPercent(parseInt(overview.dpd31_60), overview.outstanding_to_investors),
                color: '#EEB624'
              },
              {
                name: this.$t('views.statistics.labelSixtyOrMore'),
                amount: parseInt(overview.buyback_initiated),
                totalPercent: this.getPercent(parseInt(overview.buyback_initiated), totalStatusesAmount),
                percent: this.getPercent(parseInt(overview.buyback_initiated), overview.outstanding_to_investors),
                color: '#750f2d'
              }
            ]
            this.statusesData.push(
                {
                  categoryName: overview.loan_originator_name,
                  data: statusesData
                }
            )
          }
      )
      this.interestPaidToInvestorData = interestPaidToInvestorData
      this.loStatsData = [{
        categoryName: 'Individual',
        data: loStatsData
      }]

      statusesData = [
        {
          name: this.$t('views.statistics.labelCurrent'),
          amount: this.statusesSum.current,
          percent: this.getPercent(this.statusesSum.current, totalStatusesAmount),
          color: '#3CCE3C'
        },
        {
          name: this.$t('views.statistics.labelOneToThirtyDays'),
          amount: this.statusesSum.dpd1_30,
          percent: this.getPercent(this.statusesSum.dpd1_30, totalStatusesAmount),
          color: '#4CAF50'
        },
        {
          name: this.$t('views.statistics.labelThirtyToSixtyDays'),
          amount: this.statusesSum.dpd31_60,
          percent: this.getPercent(this.statusesSum.dpd31_60, totalStatusesAmount),
          color: '#EEB624'
        },
        {
          name: this.$t('views.statistics.labelSixtyOrMore'),
          amount: this.statusesSum.buyback_initiated,
          percent: this.getPercent(this.statusesSum.buyback_initiated, totalStatusesAmount),
          color: '#750f2d'
        }
      ]

      this.statusesData.unshift({
        categoryName: this.$t('views.statistics.allLoanOriginators'),
        data: statusesData
      })
      this.isLoading = false
    }).catch(error =>
    {
      // Zero-valued figures would read as real data, so the page must say it failed.
      console.error('Statistics load failed', error)
      this.isLoading = false
      this.loadFailed = true
    })
  },
  methods:
      {
        getPercent(amount, total)
        {
          try
          {
            if (!total) return 0
            return (amount * 100 / total).toFixed(2)
          } catch (e)
          {
            console.log('Failed to get percent in amount:', amount, ' for total:', total, ' error:', e)
          }
        }
      }
}
</script>
<style src="../assets/css/statistics.css" ></style>
<style lang="scss">

.radio-assets {
  text-align: center;

  .btn.focus,
  .btn-outline-primary:hover {
    box-shadow: none !important;
  }

  .btn-none {
    color: #30A7FE !important;
    border-color: #30A7FE !important;
  }

  .active {
    background-color: #30A7FE !important;
    border-color: #30A7FE !important;
    color: white !important;
  }
}
</style>
<style scoped lang="scss">
.chart {
  margin-top: 50px;
  width: 100%;
  height: 400px;
}

.table-header {
  font-style: normal;
  font-weight: bold;
  font-size: 13px;
  line-height: 10px;
  text-align: center;
  /*text-transform: uppercase;*/
  color: #9DA0A6;
}

.table-cell {
  font-style: normal;
  line-height: 10px;
}

.stat-under-header {
  margin-top: -16px;
}

.lifetime-header {
  border-right: 1px solid #c3c3c3
}

.help-tooltip {
  width: 18px;
  height: 18px;
  margin-top: 4px;
  cursor: pointer;
}

.home-start-investing {
  text-align: center;
  margin-top: 80px;
  margin-bottom: 100px;
  font-style: normal;

  h2 {
    font-size: 36px;
    line-height: 40px;
    font-weight: bold;
    color: #000000;
  }

  .start-investing-img {
    margin-bottom: 40px;
    width: 100%;
    max-width: 590px;
  }

  .detail-info {
    font-weight: normal;
    font-size: 21px;
    line-height: 28px;
    color: #9DA0A6;
  }
}

[lang="de"] {
  @media screen and (min-width: 991px) {
    .price-graphics h3 {
      height: 60px;
    }
  }
}
</style>
